import axios from 'axios';
import useAuthStore from '../store/auth';
import Auth from './Auth';
import useSignupStore from '../store/signup';

const authUrls = [
  '/auth/login/',
  '/auth/logout/',
  '/auth/token/refresh/',
  '/api/token/refresh/',
  '/auth/password/reset/',
];

let activeRefreshPromise = null;

const refreshAccessToken = () => {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = (async () => {
    try {
      const refreshToken = Auth.refreshToken;

      if (!refreshToken) {
        return null;
      }

      const res = await ax.post('/auth/token/refresh/', {
        refresh: refreshToken,
      });

      const access = res.data.access;
      Auth.accessToken = access;
      return access;
    } catch (e) {
      console.error('Failed to refresh access token:', e);
      useAuthStore.getState().logout();
      return null;
    } finally {
      activeRefreshPromise = null;
    }
  })();

  return activeRefreshPromise;
};

const formatAuthHeader = (token) => {
  if (token.startsWith('Bearer ') || token.startsWith('Token ')) {
    return token;
  }
  if (process.env.REACT_APP_AUTH_PREFIX !== undefined) {
    const prefix = process.env.REACT_APP_AUTH_PREFIX;
    return prefix === '' ? token : `${prefix} ${token}`;
  }
  const isDjangoToken = token.length === 40 && /^[a-f0-9]{40}$/i.test(token);
  return isDjangoToken ? `Token ${token}` : `Bearer ${token}`;
};

const ax = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

ax.interceptors.request.use((config) => {
  // CRITICAL: Sanitize URL FIRST - ensure no spaces (replace with underscores)
  // This must happen before any other processing to prevent 404 errors
  if (config.url) {
    const originalUrl = config.url;
    // Replace all spaces, tabs, and other whitespace with underscores
    // This fixes URLs like "check user signup status" → "check_user_signup_status"
    config.url = String(config.url).replace(/[\s\t\n\r]+/g, '_');

    // Also sanitize query string parameters in the URL
    if (config.url.includes('?')) {
      const [baseUrl, queryString] = config.url.split('?');
      if (queryString) {
        // Sanitize query string - replace spaces in parameter values
        const sanitizedQuery = queryString
          .split('&')
          .map((param) => {
            const [key, value] = param.split('=');
            const sanitizedValue = value
              ? decodeURIComponent(value).replace(/[\s\t\n\r]+/g, '_')
              : value;
            return `${key}=${encodeURIComponent(sanitizedValue)}`;
          })
          .join('&');
        config.url = `${baseUrl}?${sanitizedQuery}`;
      }
    }

    // Also sanitize the baseURL if it exists
    if (config.baseURL && config.baseURL.includes(' ')) {
      config.baseURL = config.baseURL.replace(/[\s\t\n\r]+/g, '_');
    }

    // Log if we had to sanitize (for debugging)
    if (originalUrl !== config.url) {
      console.warn(
        '⚠️ URL sanitized (spaces removed):',
        originalUrl,
        '→',
        config.url
      );
    }
  }

  // Skip auth token attachment for authentication endpoints
  if (authUrls.includes(config.url) || config.url?.includes('/api/token/')) {
    return config;
  }

  const accessToken =
    Auth.accessToken ||
    useSignupStore.getState().authToken ||
    localStorage.getItem('signup_accessToken');

  if (accessToken) {
    config.headers.Authorization = formatAuthHeader(accessToken);
  }

  // Only set Content-Type to application/json if the request data is not FormData
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

ax.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config: originalRequest, response } = error;

    // Special handling for 401 errors
    if (response?.status === 401) {
      const authHeader = originalRequest?.headers?.Authorization;
      const hasTokenFromApiToken =
        !!useSignupStore.getState().authToken ||
        !!localStorage.getItem('signup_accessToken');
      console.error('🔴 401 Unauthorized:', {
        url: originalRequest?.url,
        hasAuthHeader: !!authHeader,
        usingTokenFromApiToken: hasTokenFromApiToken,
        authHeaderPreview: authHeader
          ? `${authHeader.substring(0, 40)}...`
          : 'NONE',
        backendResponse: response?.data,
      });

      if (!hasTokenFromApiToken) {
        console.warn(
          '⚠️ Using token from /auth/login/ instead of /api/token/. Token may be invalid. Please log out and log back in.'
        );
      }
    } else if (response?.status === 500) {
      // Special handling for 500 errors - backend server error
      console.error('🔴 500 Internal Server Error:', {
        url: originalRequest?.url,
        message: 'Backend server error. Check backend logs for details.',
        responseType:
          typeof response.data === 'string' &&
          response.data.includes('<!DOCTYPE')
            ? 'HTML Error Page'
            : 'JSON',
      });
    } else if (response?.status === 404) {
      // 404 errors are usually expected for optional endpoints
      // Only log if it's not an optional endpoint
      const optionalEndpoints = [
        '/api/users/dashboard-view/',
        '/api/users/check_user_signup_status/',
      ];
      if (
        !optionalEndpoints.some((endpoint) =>
          originalRequest?.url?.includes(endpoint)
        )
      ) {
        console.warn('⚠️ 404 Not Found:', originalRequest?.url);
      }
    } else {
      // Only log other errors if they're not network errors
      if (response) {
        console.error('Axios Error:', {
          url: originalRequest?.url,
          status: response.status,
          message: error.message,
        });
      }
    }

    // Handle CORS or network errors
    if (!response) {
      console.error(
        'Network Error - Check if backend is running and CORS is configured:',
        {
          url: originalRequest?.url,
          baseURL: originalRequest?.baseURL,
          fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
        }
      );
      // Don't logout on network errors during login/signup
      if (authUrls.some((url) => originalRequest?.url?.includes(url))) {
        return Promise.reject(error);
      }
    }

    if (response?.status === 503) {
      window.location.href = '/503';
    }

    // Only try to refresh token if we have a 401 and it's not a login/auth endpoint
    if (
      originalRequest.url !== '/auth/token/refresh/' &&
      !originalRequest._retry &&
      response?.status === 401 &&
      !authUrls.some((url) => originalRequest.url?.includes(url))
    ) {
      originalRequest._retry = true;
      const access = await refreshAccessToken();

      if (access) {
        const tokenValue = formatAuthHeader(access);
        ax.defaults.headers.common['Authorization'] = tokenValue;
        originalRequest.headers.Authorization = tokenValue;
        return ax(originalRequest);
      } else if (!window.location.pathname.includes('/login')) {
        useAuthStore.getState().logout();
      }
    }

    return Promise.reject(error);
  }
);

export default ax;
